'use client';

import { useCallback, useMemo, useEffect } from 'react';
import Script from 'next/script';
import { Calendar } from 'lucide-react';
import { toast } from 'sonner';

// Calendly branding colors (matching CISCODE brand)
const CALENDLY_CONFIG = {
  textColor: '08264c',        // brand-primary
  primaryColor: '3f74ef',     // brand-secondary
  hideGdprBanner: true,
} as const;

interface CalendlyPopupProps {
  /** Calendly scheduling URL (without query params) */
  url?: string;
  /** Button text */
  buttonText?: string;
  /** Button variant */
  variant?: 'primary' | 'secondary' | 'outline';
  /** Additional button classes */
  className?: string;
  /** Hide GDPR/cookie banner */
  hideGdprBanner?: boolean;
  /** Custom text color (hex without #) */
  textColor?: string;
  /** Custom primary color (hex without #) */
  primaryColor?: string;
  /** Prefill data */
  prefill?: {
    name?: string;
    email?: string;
    customAnswers?: Record<string, string>;
  };
}

declare global {
  interface Window {
    Calendly?: {
      initPopupWidget: (options: { url: string }) => void;
      showPopupWidget: (url: string, options?: {
        prefill?: {
          name?: string;
          email?: string;
          customAnswers?: Record<string, string>;
        };
      }) => void;
    };
  }

  interface WindowEventMap {
    'calendly.event_scheduled': CustomEvent<{
      event: {
        uri: string;
      };
      invitee: {
        uri: string;
        name: string;
        email: string;
      };
    }>;
  }
}

/**
 * Builds a Calendly URL with custom styling and GDPR settings
 */
function buildCalendlyUrl(
  baseUrl: string,
  options: {
    hideGdprBanner?: boolean;
    textColor?: string;
    primaryColor?: string;
  }
): string {
  const url = new URL(baseUrl);
  
  if (options.hideGdprBanner) {
    url.searchParams.set('hide_gdpr_banner', '1');
  }
  if (options.textColor) {
    url.searchParams.set('text_color', options.textColor);
  }
  if (options.primaryColor) {
    url.searchParams.set('primary_color', options.primaryColor);
  }
  
  return url.toString();
}

export default function CalendlyPopup({
  url = process.env.NEXT_PUBLIC_CALENDLY_URL || 'https://calendly.com/contact-ciscode/new-meeting',
  buttonText = 'Schedule a Call',
  variant = 'secondary',
  className = '',
  hideGdprBanner = CALENDLY_CONFIG.hideGdprBanner,
  textColor = CALENDLY_CONFIG.textColor,
  primaryColor = CALENDLY_CONFIG.primaryColor,
  prefill,
}: CalendlyPopupProps) {
  // Memoize the full URL with query params
  const fullUrl = useMemo(
    () => buildCalendlyUrl(url, { hideGdprBanner, textColor, primaryColor }),
    [url, hideGdprBanner, textColor, primaryColor]
  );

  // Listen for Calendly messages via postMessage
  useEffect(() => {
    const handleCalendlyMessage = async (e: MessageEvent) => {
      // Only process messages from Calendly
      if (e.origin !== 'https://calendly.com') {
        return;
      }

      // Check if this is an event scheduled message
      if (e.data?.event === 'calendly.event_scheduled') {
        try {
          // Track the booking in backend for rate limiting
          const response = await fetch('/api/calendly-track', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              invitee: e.data.payload?.invitee || {},
              event: e.data.payload?.event || {},
            }),
          });

          const result = await response.json();

          if (!response.ok) {
            // Rate limit exceeded
            if (response.status === 429) {
              toast.error('Booking limit reached', {
                description: result.message || 'You have reached the maximum number of bookings. Please try again later.',
                duration: 10000,
              });
            }
          } else {
            // Success - booking tracked
            toast.success('Meeting scheduled!', {
              description: 'Your booking has been confirmed.',
              duration: 5000,
            });
          }
        } catch (error) {
          // Silent error - user already got feedback
        }
      }
    };

    // Listen for postMessage from Calendly iframe
    window.addEventListener('message', handleCalendlyMessage);

    return () => {
      window.removeEventListener('message', handleCalendlyMessage);
    };
  }, []);

  const openCalendly = useCallback(async () => {
    if (typeof window === 'undefined' || !window.Calendly) {
      toast.error('Calendly is not available. Please refresh the page.');
      return;
    }

    // Check rate limit BEFORE opening popup
    try {
      const checkResponse = await fetch('/api/calendly-track', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          checkOnly: true, // Just check, don't increment
        }),
      });

      const checkResult = await checkResponse.json();

      if (!checkResponse.ok && checkResponse.status === 429) {
        // Rate limit exceeded - don't open popup
        toast.error('Booking limit reached', {
          description: checkResult.message || 'You have reached the maximum number of bookings. Please try again later.',
          duration: 10000,
        });
        return;
      }
    } catch (error) {
      // Continue anyway if check fails
    }

    // Open Calendly popup
    window.Calendly.showPopupWidget(fullUrl, prefill ? { prefill } : undefined);
  }, [fullUrl, prefill]);

  const variantClasses = {
    primary: 'bg-brand-gradient text-white shadow-[0_16px_32px_rgba(53,30,224,0.35)] hover:shadow-[0_22px_40px_rgba(53,30,224,0.45)]',
    secondary: 'bg-white text-brand-primary border-2 border-brand-primary hover:bg-brand-primary hover:text-white',
    outline: 'bg-transparent text-brand-primary border border-brand-primary hover:bg-brand-primary hover:text-white',
  };

  return (
    <>
      <button
        type="button"
        onClick={openCalendly}
        className={`inline-flex items-center justify-center gap-2 h-14 px-6 rounded-[5px] font-button font-semibold text-sm sm:text-base transition-all duration-200 hover:scale-[1.01] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-secondary focus-visible:ring-offset-2 disabled:opacity-70 disabled:cursor-not-allowed ${variantClasses[variant]} ${className}`}
      >
        <Calendar className="w-5 h-5" />
        {buttonText}
      </button>

      {/* Calendly Widget Styles */}
      <link
        href="https://assets.calendly.com/assets/external/widget.css"
        rel="stylesheet"
      />

      {/* Calendly Widget Script */}
      <Script
        src="https://assets.calendly.com/assets/external/widget.js"
        strategy="lazyOnload"
      />
    </>
  );
}
